import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/NumericInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// Deep-compare helper for dirty detection
function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a == b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === 'object') {
    const ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every(k => deepEqual(a[k], b[k]));
  }
  return false;
}

const CurtainwallPanelDetailDialog = ({
  open,
  onOpenChange,
  panel: panelProp,
  onSave,
  cwGlassTypes = [],
  cwMetalTypes = []
}) => {
  const [formData, setFormData] = useState({
    width_in: null, height_in: null,
    roWidth_in: null, roHeight_in: null,
    lites: []
  });
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const initialSnapshot = useRef(null);

  // Build normalized form state from panel prop
  const buildFormState = useCallback((p) => {
    let lites = [];
    if (p.lites && p.lites.length > 0) {
      lites = p.lites.map((lite, i) => ({
        id: i + 1,
        liteType: lite.liteType || 'glass',
        width_in: lite.width_in ?? '',
        height_in: lite.height_in ?? '',
        glassTypeId: lite.glassTypeId || '',
        metalPanelTypeId: lite.metalPanelTypeId || ''
      }));
    } else if (p.panelType) {
      // Backward compat: legacy single-panel record (panelType=GL/MT) → synthesize one lite
      lites = [{
        id: 1,
        liteType: p.panelType === 'MT' ? 'metalPanel' : 'glass',
        width_in: p.width_in ?? '',
        height_in: p.height_in ?? '',
        glassTypeId: p.glassTypeId || '',
        metalPanelTypeId: p.metalTypeId || ''
      }];
    }
    return {
      width_in: p.width_in || null,
      height_in: p.height_in || null,
      roWidth_in: p.roWidth_in || null,
      roHeight_in: p.roHeight_in || null,
      lites
    };
  }, []);

  useEffect(() => {
    if (panelProp && open) {
      const state = buildFormState(panelProp);
      setFormData(state);
      initialSnapshot.current = JSON.parse(JSON.stringify(state));
      setTouched({});
      setSaving(false);
      setShowDiscardConfirm(false);
    }
  }, [panelProp, open, buildFormState]);

  // Dirty check
  const isDirty = useMemo(() => {
    if (!initialSnapshot.current) return false;
    return !deepEqual(formData, initialSnapshot.current);
  }, [formData]);

  // --- Intercept all close attempts ---
  const attemptClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // --- Lite CRUD ---
  const handleAddLite = () => {
    const newId = formData.lites.length > 0
      ? Math.max(...formData.lites.map(l => l.id)) + 1 : 1;
    setFormData({
      ...formData,
      lites: [...formData.lites, { id: newId, liteType: 'glass', width_in: '', height_in: '', glassTypeId: '', metalPanelTypeId: '' }]
    });
  };

  const handleRemoveLite = (id) => {
    setFormData({ ...formData, lites: formData.lites.filter(l => l.id !== id) });
    setTouched(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleLiteChange = (id, field, value) => {
    setTouched(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: true } }));
    const updated = formData.lites.map(lite => {
      if (lite.id === id) {
        if (field === 'liteType') {
          return { ...lite, [field]: value, glassTypeId: value === 'glass' ? lite.glassTypeId : '', metalPanelTypeId: value === 'metalPanel' ? lite.metalPanelTypeId : '' };
        }
        return { ...lite, [field]: value };
      }
      return lite;
    });
    setFormData({ ...formData, lites: updated });
  };

  // --- Validation ---
  // Note: 0/negative values are validated by the native HTML5 balloon (NumericInput positive prop).
  // This function only checks for missing/required fields shown as inline red text.
  const getLiteErrors = (lite) => {
    const errors = {};
    const w = parseFloat(lite.width_in);
    const h = parseFloat(lite.height_in);
    if (lite.width_in === '' || lite.width_in === null || isNaN(w)) errors.width_in = 'This field is required';
    if (lite.height_in === '' || lite.height_in === null || isNaN(h)) errors.height_in = 'This field is required';
    if (!lite.liteType) errors.liteType = 'This field is required';
    if (lite.liteType === 'glass' && !lite.glassTypeId) errors.glassTypeId = 'This field is required';
    if (lite.liteType === 'metalPanel' && !lite.metalPanelTypeId) errors.metalPanelTypeId = 'This field is required';
    return errors;
  };

  const hasLiteErrors = useMemo(() => formData.lites.some(l => Object.keys(getLiteErrors(l)).length > 0), [formData.lites]);

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.lites.length > 0) {
      const allTouched = {};
      formData.lites.forEach(l => { allTouched[l.id] = { width_in: true, height_in: true, liteType: true, glassTypeId: true, metalPanelTypeId: true }; });
      setTouched(allTouched);
      if (hasLiteErrors) { toast.error('Please fix the highlighted fields'); return; }
    }
    setSaving(true);
    try {
      await onSave(formData);
      // Reset dirty state on success
      initialSnapshot.current = JSON.parse(JSON.stringify(formData));
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) { attemptClose(); return; }
          onOpenChange(true);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => { if (isDirty) { e.preventDefault(); attemptClose(); } }}>
          <DialogHeader>
            <DialogTitle>Curtainwall Details: {panelProp?.label}</DialogTitle>
            <DialogDescription>Enter dimensions and lite specifications for this curtainwall</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Curtainwall Dimensions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Curtainwall Dimensions</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Curtainwall Width (in)</Label>
                  <NumericInput positive allowFraction value={formData.width_in || ''} onChange={(v) => setFormData({ ...formData, width_in: v })} placeholder="e.g., 120.0" data-testid="cw-detail-width" />
                </div>
                <div className="space-y-2">
                  <Label>Curtainwall Height (in)</Label>
                  <NumericInput positive allowFraction value={formData.height_in || ''} onChange={(v) => setFormData({ ...formData, height_in: v })} placeholder="e.g., 96.0" data-testid="cw-detail-height" />
                </div>
                <div className="space-y-2">
                  <Label>Rough Opening Width (in)</Label>
                  <NumericInput positive allowFraction value={formData.roWidth_in || ''} onChange={(v) => setFormData({ ...formData, roWidth_in: v })} placeholder="e.g., 121.0" data-testid="cw-detail-ro-width" />
                </div>
                <div className="space-y-2">
                  <Label>Rough Opening Height (in)</Label>
                  <NumericInput positive allowFraction value={formData.roHeight_in || ''} onChange={(v) => setFormData({ ...formData, roHeight_in: v })} placeholder="e.g., 97.0" data-testid="cw-detail-ro-height" />
                </div>
              </div>
            </div>

            {/* Lites */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <div>
                  <h3 className="font-semibold text-lg">Lites</h3>
                  <p className="text-xs text-slate-500 mt-1">Add glass or metal panel lites for this curtainwall</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleAddLite} className="flex items-center gap-1" data-testid="cw-add-lite-btn">
                  <Plus className="h-4 w-4" /> Add Lite
                </Button>
              </div>

              {formData.lites.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[100px_100px_100px_120px_1fr_40px] gap-2 bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 uppercase">
                    <div></div><div>Width (in) *</div><div>Height (in) *</div><div>Lite Type *</div><div>Glass / Metal Panel *</div><div></div>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {formData.lites.map((lite, index) => {
                      const errors = getLiteErrors(lite);
                      const t = touched[lite.id] || {};
                      return (
                        <div key={lite.id} className="grid grid-cols-[100px_100px_100px_120px_1fr_40px] gap-2 px-3 py-2 items-start bg-white hover:bg-slate-50">
                          <div className="text-sm font-medium text-slate-700 pt-2">Lite {index + 1}</div>
                          <div>
                            <NumericInput positive allowFraction value={lite.width_in} onChange={(v) => handleLiteChange(lite.id, 'width_in', v)} placeholder="0" className={`h-8 text-sm ${t.width_in && errors.width_in ? 'border-red-500' : ''}`} data-testid={`cw-lite-${index}-width`} />
                            {t.width_in && errors.width_in && <p className="text-[10px] text-red-500 mt-0.5">{errors.width_in}</p>}
                          </div>
                          <div>
                            <NumericInput positive allowFraction value={lite.height_in} onChange={(v) => handleLiteChange(lite.id, 'height_in', v)} placeholder="0" className={`h-8 text-sm ${t.height_in && errors.height_in ? 'border-red-500' : ''}`} data-testid={`cw-lite-${index}-height`} />
                            {t.height_in && errors.height_in && <p className="text-[10px] text-red-500 mt-0.5">{errors.height_in}</p>}
                          </div>
                          <div>
                            <Select value={lite.liteType || 'glass'} onValueChange={(val) => handleLiteChange(lite.id, 'liteType', val)} data-testid={`cw-lite-${index}-type`}>
                              <SelectTrigger className={`h-8 text-sm ${t.liteType && errors.liteType ? 'border-red-500' : ''}`}><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="glass">Glass</SelectItem><SelectItem value="metalPanel">Metal Panel</SelectItem></SelectContent>
                            </Select>
                            {t.liteType && errors.liteType && <p className="text-[10px] text-red-500 mt-0.5">{errors.liteType}</p>}
                          </div>
                          <div>
                            {lite.liteType === 'metalPanel' ? (
                              <>
                                <Select value={lite.metalPanelTypeId || 'none'} onValueChange={(val) => handleLiteChange(lite.id, 'metalPanelTypeId', val === 'none' ? '' : val)} data-testid={`cw-lite-${index}-metal-panel`}>
                                  <SelectTrigger className={`h-8 text-sm ${t.metalPanelTypeId && errors.metalPanelTypeId ? 'border-red-500' : ''}`}><SelectValue placeholder="Select Metal Panel" /></SelectTrigger>
                                  <SelectContent><SelectItem value="none">None</SelectItem>{cwMetalTypes.map(mp => <SelectItem key={mp.id} value={mp.id}>{mp.name}</SelectItem>)}</SelectContent>
                                </Select>
                                {t.metalPanelTypeId && errors.metalPanelTypeId && <p className="text-[10px] text-red-500 mt-0.5">{errors.metalPanelTypeId}</p>}
                              </>
                            ) : (
                              <>
                                <Select value={lite.glassTypeId || 'none'} onValueChange={(val) => handleLiteChange(lite.id, 'glassTypeId', val === 'none' ? '' : val)} data-testid={`cw-lite-${index}-glass-type`}>
                                  <SelectTrigger className={`h-8 text-sm ${t.glassTypeId && errors.glassTypeId ? 'border-red-500' : ''}`}><SelectValue placeholder="Select Glass Type" /></SelectTrigger>
                                  <SelectContent><SelectItem value="none">None</SelectItem>{cwGlassTypes.map(gt => <SelectItem key={gt.id} value={gt.id}>{gt.name}</SelectItem>)}</SelectContent>
                                </Select>
                                {t.glassTypeId && errors.glassTypeId && <p className="text-[10px] text-red-500 mt-0.5">{errors.glassTypeId}</p>}
                              </>
                            )}
                          </div>
                          <div className="flex justify-center pt-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveLite(lite.id)} className="h-8 w-8 p-0 hover:bg-red-500 hover:text-white transition-colors" data-testid={`cw-lite-${index}-remove`}>
                              <Trash2 className="h-4 w-4 text-red-500 hover:text-white" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg bg-slate-50">
                  <p className="text-slate-500 text-sm">No lites added yet</p>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddLite} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add First Lite</Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={attemptClose} disabled={saving} data-testid="cw-detail-cancel-btn">Cancel</Button>
              <Button type="submit" className="bg-[#FF6600] hover:bg-[#ff8533]" disabled={saving} data-testid="save-cw-details-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to exit without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cw-continue-editing-btn">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-red-600 hover:bg-red-700" data-testid="cw-discard-changes-btn">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CurtainwallPanelDetailDialog;
